const {
  HP_TOP_BANNER,
  HP_HERO,
  RESPONSIVE_CAROUSEL,
  IN_YOUR_BAG,
  LOOKS_REC_TRAY,
  RECOMMENDATION_SHELF,
  HP_RANKED_RESULTS,
  PROMO_TILE,
  HP_SERVICES,
  MORE_TO_EXPLORE,
  STORY_CAROUSEL,
  PHP_TILE,
  PHP_TILE_PREVIEW,
  REMAINING_COMPONENTS
} = require('./constants');
const { matchTemplate, matchSchema, matchId } = require('./helpers');
const { personalizedTypes, phpTileMapper } = require('./phpTileMapper');

/**
 * getHpTopBannerPlacement - This method looks for a Tile that has "HP - P00 Banner" as the
 * TemplateName. The function splices the matching componentData from the pool and adds it to
 * the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getHpTopBannerPlacement = function getHpTopBannerPlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Find a match
  const matchIndex = pool.findIndex(matchTemplate(HP_TOP_BANNER));

  if (matchIndex !== -1) {
    result.push(pool.splice(matchIndex, 1)[0]);
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getHpHeroPlacement - This method looks for a Tile that has the "HP - Default Hero" as the
 * TemplateName. If there's no match, it looks for a Responsive Carousel. If neither a matching
 * Tile nor a Responsive Carousel is available, this method takes the first (highest scoring)
 * Personal HP Tile as a match. The function splices the matching componentData from the pool and
 * adds it to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getHpHeroPlacement = function getHpHeroPlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Find a match
  const matchedForcedHeroIndex = pool.findIndex(matchTemplate(HP_HERO));
  const matchedCarouselIndex = pool.findIndex(matchSchema(RESPONSIVE_CAROUSEL));
  const fallbackPhpTileIndex = pool.findIndex(matchSchema(PHP_TILE));

  if (matchedForcedHeroIndex !== -1) {
    result.push(pool.splice(matchedForcedHeroIndex, 1)[0]);
  } else if (matchedCarouselIndex !== -1) {
    result.push(pool.splice(matchedCarouselIndex, 1)[0]);
  } else if (fallbackPhpTileIndex !== -1) {
    const component = pool.splice(fallbackPhpTileIndex, 1)[0];

    result.push(phpTileMapper(component, personalizedTypes.hero));
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getInYourBagPlacement - This method adds an In Your Bag component to the result without
 * requiring a match.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getInYourBagPlacement = function getInYourBagPlacement(accumulator) {
  const inYourBagData = {
    SchemaName: IN_YOUR_BAG
  };

  return {
    ...accumulator,
    result: [...accumulator.result, inYourBagData]
  };
};

/**
 * getLooksRecTrayPlacement - This method looks for a Looks Rec Tray. The function splices the
 * matching componentData from the pool and adds it to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getLooksRecTrayPlacement = function getLooksRecTrayPlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Find a match
  const matchIndex = pool.findIndex(matchSchema(LOOKS_REC_TRAY));

  if (matchIndex !== -1) {
    result.push(pool.splice(matchIndex, 1)[0]);
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getRecTrayPlacement - This method looks for Product Recommendations Shelf (Recommendations 3.0).
 * The function splices the matching componentData from the pool and adds it to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getRecTrayPlacement = function getRecTrayPlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];
  let { recPlacementIndex } = accumulator;

  // Find a match
  const matchIndex = pool.findIndex(({ SchemaName }) =>
    /Recommendations 3.0/.test(SchemaName)
  );

  if (matchIndex !== -1) {
    const component = pool.splice(matchIndex, 1)[0];

    recPlacementIndex++;
    result.push({ ...component, recPlacementIndex });
  }

  return {
    ...accumulator,
    recPlacementIndex,
    pool,
    result
  };
};

/**
 * getHpRankedResults - This method looks for a Personalized HP Tile. The function modifies the
 * matching componentData from the pool into a regular Tile component using the phpTileMapper
 * and adds the showProductTray prop to change it to a Tile + Ranked Results component. The
 * component is then added to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getHpRankedResults = function getHpRankedResults(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Find a match
  const matchIndex = pool.findIndex(matchSchema(PHP_TILE));

  if (matchIndex !== -1) {
    const component = pool.splice(matchIndex, 1)[0];

    // set showProductTray to enable story ranked results
    result.push({
      ...phpTileMapper(component, personalizedTypes.vertical),
      showProductTray: true
    });
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getPromoPlacement - This method looks for a Tile that has "PromoTile" as the TemplateName. The
 * function chooses a random match from the whole list instead of taking the first match. It is
 * then spliced from the pool and added it to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getPromoPlacement = function getPromoPlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Filter for matching components
  const matches = pool.filter(matchTemplate(PROMO_TILE));

  if (matches.length) {
    // Pick a random component among matches and match to the pool by Id
    const randomIndex = Math.floor(Math.random() * matches.length);
    const id = matches[randomIndex].Id;
    const matchIndex = pool.findIndex(matchId(id));

    result.push(pool.splice(matchIndex, 1)[0]);
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getHpServicePlacement - This method looks for a Tile that has "HP - Services'" as the
 * TemplateName. The function chooses a random match from the whole list instead of taking the
 * first match. It is then spliced from the pool and added it to the result.
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getHpServicePlacement = function getHpServicePlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const result = [...accumulator.result];

  // Filter for matching components
  const matches = pool.filter(matchTemplate(HP_SERVICES));

  if (matches.length) {
    // Pick a random component among matches and match to the pool by Id
    const randomIndex = Math.floor(Math.random() * matches.length);
    const id = matches[randomIndex].Id;
    const matchIndex = pool.findIndex(matchId(id));

    result.push(pool.splice(matchIndex, 1)[0]);
  }

  return {
    ...accumulator,
    pool,
    result
  };
};

/**
 * getMoreToExplorePlacement - This method creates a Story Carousel component and looks for up to
 * 9 Personalized HP Tiles to fill it as children. The matching Personalized HP Tiles are changed
 * to regular Tiles using the phpTileMapper and spliced from the available pool. The Headline for
 * the created Story Carousel is hard-coded to "More to Explore".
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getMoreToExplorePlacement = function getMoreToExplorePlacement(accumulator) {
  // Copy arrays from the accumulator
  const pool = [...accumulator.pool];
  const children = [];
  let hasMatch = true;

  // Find 9 matches to fill more to explore
  do {
    const matchIndex = pool.findIndex(matchTemplate(PHP_TILE));

    hasMatch = matchIndex !== -1;

    if (hasMatch) {
      const component = pool.splice(matchIndex, 1)[0];

      children.push(phpTileMapper(component, personalizedTypes.hero));
    }
  } while (hasMatch && children.length < 9);

  const storyCarouselData = {
    SchemaName: STORY_CAROUSEL,
    headline: MORE_TO_EXPLORE,
    tiles: children
  };

  return {
    ...accumulator,
    pool,
    result: [...accumulator.result, storyCarouselData]
  };
};

/**
 * getAllPhpTilePreviewPlacement - This method creates 2 tiles for each personalized homepage tile
 * data. The first is a regular tile using the square hero image. The second is a story plus ranked
 * results tile using the vertical image. The placement will do this for every personalized
 * homepage tile without modifying the available pool
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getAllPhpTilePreviewPlacement = function getAllPhpTilePreviewPlacement(accumulator) {
  const phpPreviewTiles = [];

  // Filter for all personalized homepage tiles
  const rawPhpTiles = accumulator.pool.filter(matchTemplate(PHP_TILE));

  rawPhpTiles.forEach(phpTile => {
    // Add a hero component to the results
    phpPreviewTiles.push(phpTileMapper(phpTile, personalizedTypes.hero));

    // Add a story plus ranked results component to the results
    phpPreviewTiles.push({
      ...phpTileMapper(phpTile, personalizedTypes.vertical),
      showProductTray: true
    });
  });

  return {
    ...accumulator,
    result: [...accumulator.result, ...phpPreviewTiles]
  };
};

/**
 * getRemainingComponentsPlacement - This method adds all components still in the pool to the
 * results. In a template on its own, it will act as a pass through without modifying data
 *
 * @param {object} accumulator - Carries over changes from any previous placements
 * @param {Array} accumulator.pool - List of componentData not previously used in placements
 * @param {Array} accumulator.result - List of modified componentData used in placements so far
 * @returns {object} - Used as the accumulator for the next placement
 */
const getRemainingComponentsPlacement = function getRemainingComponentsPlacement(accumulator) {
  return {
    ...accumulator,
    result: [...accumulator.result, ...accumulator.pool],
    pool: []
  };
};

module.exports = {
  [HP_TOP_BANNER]: getHpTopBannerPlacement,
  [HP_HERO]: getHpHeroPlacement,
  [IN_YOUR_BAG]: getInYourBagPlacement,
  [LOOKS_REC_TRAY]: getLooksRecTrayPlacement,
  [RECOMMENDATION_SHELF]: getRecTrayPlacement,
  [HP_RANKED_RESULTS]: getHpRankedResults,
  [PROMO_TILE]: getPromoPlacement,
  [HP_SERVICES]: getHpServicePlacement,
  [MORE_TO_EXPLORE]: getMoreToExplorePlacement,
  [PHP_TILE_PREVIEW]: getAllPhpTilePreviewPlacement,
  [REMAINING_COMPONENTS]: getRemainingComponentsPlacement
};
